/**
 * SmartLink Router — data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the `/api/v1/smartlink-router`
 * surface (FS-101 / feature #101). Mirrors the contract declared in
 * `apps/api/src/routes/smartlink-router.ts`.
 *
 * Endpoints exercised:
 *   GET    /                             List smartlinks for the tenant
 *   POST   /                             Create smartlink
 *                                         body { slug, name, policy? }
 *   PATCH  /:id                          Update name / policy / active
 *   DELETE /:id                          Soft-delete smartlink
 *   POST   /:id/candidates               Add candidate
 *                                         body { offer_id, weight, payout_cents?,
 *                                                eligibility?, active }
 *   DELETE /:id/candidates/:cid          Deactivate candidate
 *   POST   /resolve                      Resolve by slug + context
 *                                         body { slug, context? }
 *
 *   (Back-compat: GET /:id/resolve and POST /:id/offers still work.)
 *
 * Keep this file dependency-free beyond the root `apiClient` — no React,
 * no TanStack — so the validators can be unit-tested in isolation.
 */

import { apiClient, ApiError } from "@/api-client";
import { z } from "zod";

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

export const SMARTLINK_POLICIES = [
	"priority",
	"weighted",
	"payout_max",
] as const;
export type SmartLinkPolicy = (typeof SMARTLINK_POLICIES)[number];

export interface Eligibility {
	geo?: string[];
	device?: string[];
	os?: string[];
}

export interface ResolveContext {
	geo?: string;
	device?: string;
	os?: string;
}

export interface SmartLinkRow {
	id: string;
	tenant_id: string;
	slug: string;
	name: string;
	policy: SmartLinkPolicy;
	active: number; // 0 | 1
	created_at: number;
	updated_at?: number | null;
	deleted_at?: number | null;
}

export interface SmartLinkCandidateRow {
	id: string;
	smartlink_id: string;
	offer_id: string;
	weight: number;
	payout_cents: number;
	eligibility: string; // JSON-serialized Eligibility
	active: number; // 0 | 1
	created_at: number;
	updated_at?: number | null;
}

/**
 * Legacy alias — the previous iteration used `SmartLinkOfferRow` and only
 * exposed `weight` / `active` / `created_at`. Keeping the alias so older
 * imports continue to compile without behavioural change.
 */
export type SmartLinkOfferRow = SmartLinkCandidateRow;

export interface ResolveResponse {
	/** FS-101 shape — always present on `POST /resolve`. */
	offer_id?: string;
	candidate_id?: string;
	redirect_url?: string;
	smartlink: {
		id: string;
		slug: string;
		name: string;
		policy?: SmartLinkPolicy;
	};
	picked: {
		id: string;
		offer_id: string;
		weight: number;
		payout_cents?: number;
	};
	pool_size: number;
}

// ---------------------------------------------------------------------------
// Form schemas — shared between the page's react-hook-form wiring and the
// unit tests. Zod is already a dash dep (package.json locked).
// ---------------------------------------------------------------------------

export const SLUG_RE = /^[a-z0-9][a-z0-9_-]{1,63}$/;
export const MAX_WEIGHT = 10_000;
/** 10 M USD in cents — high enough that no realistic payout trips it. */
export const MAX_PAYOUT_CENTS = 1_000_000_000;

export const createSmartlinkSchema = z.object({
	slug: z
		.string()
		.trim()
		.min(2, "Slug must be at least 2 characters.")
		.max(64, "Slug must be 64 characters or fewer.")
		.regex(SLUG_RE, "Use lowercase letters, digits, dashes, underscores."),
	name: z
		.string()
		.trim()
		.min(1, "Name is required.")
		.max(200, "Name must be 200 characters or fewer."),
	policy: z.enum(SMARTLINK_POLICIES).default("weighted"),
});
export type CreateSmartlinkInput = z.infer<typeof createSmartlinkSchema>;

/** Text-form eligibility — user types comma-separated lists per dimension. */
export const eligibilityInputSchema = z.object({
	geo: z.string().trim().max(256).default(""),
	device: z.string().trim().max(256).default(""),
	os: z.string().trim().max(256).default(""),
});
export type EligibilityInput = z.infer<typeof eligibilityInputSchema>;

/**
 * Parses a comma/space/newline separated tag list into a deduped string array.
 * Blank → empty array. Used by the rules-builder widget.
 */
export function parseTagList(raw: string): string[] {
	if (!raw) return [];
	const tokens = raw
		.split(/[,\n\s]+/)
		.map((t) => t.trim())
		.filter((t) => t.length > 0);
	const out: string[] = [];
	for (const t of tokens) if (!out.includes(t)) out.push(t);
	return out;
}

export function buildEligibility(input: EligibilityInput): Eligibility {
	const geo = parseTagList(input.geo).map((s) => s.toUpperCase());
	const device = parseTagList(input.device).map((s) => s.toLowerCase());
	const os = parseTagList(input.os).map((s) => s.toLowerCase());
	const out: Eligibility = {};
	if (geo.length) out.geo = geo;
	if (device.length) out.device = device;
	if (os.length) out.os = os;
	return out;
}

export const attachCandidateSchema = z.object({
	offer_id: z
		.string()
		.trim()
		.min(1, "Offer ID is required.")
		.max(120, "Offer ID must be 120 characters or fewer."),
	weight: z
		.number({ error: "Weight must be a whole number." })
		.int("Weight must be a whole number.")
		.min(1, "Weight must be at least 1.")
		.max(MAX_WEIGHT, `Weight must be ${MAX_WEIGHT} or less.`),
	payout_cents: z
		.number({ error: "Payout must be a whole number." })
		.int("Payout must be a whole number of cents.")
		.min(0, "Payout cannot be negative.")
		.max(MAX_PAYOUT_CENTS, "Payout is unrealistically large.")
		.default(0),
	active: z.boolean(),
	eligibility_geo: z.string().trim().max(256).default(""),
	eligibility_device: z.string().trim().max(256).default(""),
	eligibility_os: z.string().trim().max(256).default(""),
});
export type AttachCandidateInput = z.infer<typeof attachCandidateSchema>;

/** Legacy alias for older imports. */
export type AttachOfferInput = AttachCandidateInput;
export const attachOfferSchema = attachCandidateSchema;

export const patchSmartlinkSchema = z
	.object({
		name: z.string().trim().min(1).max(200).optional(),
		policy: z.enum(SMARTLINK_POLICIES).optional(),
		active: z.boolean().optional(),
	})
	.refine(
		(v) =>
			v.name !== undefined || v.policy !== undefined || v.active !== undefined,
		{ message: "Nothing to update." },
	);
export type PatchSmartlinkInput = z.infer<typeof patchSmartlinkSchema>;

export const resolveContextSchema = z.object({
	slug: z
		.string()
		.trim()
		.min(2)
		.max(64)
		.regex(SLUG_RE, "Invalid slug."),
	geo: z.string().trim().max(64).default(""),
	device: z.string().trim().max(64).default(""),
	os: z.string().trim().max(64).default(""),
});
export type ResolveContextInput = z.infer<typeof resolveContextSchema>;

// ---------------------------------------------------------------------------
// Pure helpers — exported so the test file can assert on them directly.
// ---------------------------------------------------------------------------

/**
 * Tallies resolve picks into a per-offer distribution ordered by count desc.
 * Stable tie-break by first-seen offer id so the UI renders deterministically.
 */
export function tallyDistribution(
	picks: readonly ResolveResponse[],
): Array<{ offer_id: string; count: number; percent: number }> {
	const total = picks.length;
	if (total === 0) return [];
	const order: string[] = [];
	const counts = new Map<string, number>();
	for (const p of picks) {
		const id = p.picked.offer_id;
		if (!counts.has(id)) order.push(id);
		counts.set(id, (counts.get(id) ?? 0) + 1);
	}
	return order
		.map((offer_id) => {
			const count = counts.get(offer_id) ?? 0;
			return {
				offer_id,
				count,
				percent: Math.round((count / total) * 1000) / 10,
			};
		})
		.sort((a, b) => b.count - a.count || a.offer_id.localeCompare(b.offer_id));
}

export function formatEligibility(raw: string): string {
	try {
		const obj = JSON.parse(raw) as Eligibility;
		const parts: string[] = [];
		if (obj.geo?.length) parts.push(`geo=${obj.geo.join(",")}`);
		if (obj.device?.length) parts.push(`device=${obj.device.join(",")}`);
		if (obj.os?.length) parts.push(`os=${obj.os.join(",")}`);
		return parts.length ? parts.join(" · ") : "any";
	} catch {
		return "any";
	}
}

export function formatPolicyLabel(p: SmartLinkPolicy): string {
	switch (p) {
		case "priority":
			return "Priority (highest weight)";
		case "weighted":
			return "Weighted random";
		case "payout_max":
			return "Highest payout";
	}
}

// ---------------------------------------------------------------------------
// Remote operations — the page imports these directly. Every call runs
// through `apiClient`, which already attaches `x-tenant-id: demo`.
// ---------------------------------------------------------------------------

const BASE = "/smartlink-router";

export async function listSmartlinks(): Promise<SmartLinkRow[]> {
	const res = await apiClient.get<{ items: SmartLinkRow[] }>(BASE);
	return res.items ?? [];
}

export async function createSmartlink(
	input: CreateSmartlinkInput,
): Promise<SmartLinkRow> {
	return apiClient.post<SmartLinkRow>(BASE, input);
}

export async function patchSmartlink(
	id: string,
	input: PatchSmartlinkInput,
): Promise<SmartLinkRow> {
	return apiClient.patch<SmartLinkRow>(
		`${BASE}/${encodeURIComponent(id)}`,
		input,
	);
}

export async function attachCandidate(
	smartlinkId: string,
	input: AttachCandidateInput,
): Promise<SmartLinkCandidateRow> {
	const eligibility = buildEligibility({
		geo: input.eligibility_geo,
		device: input.eligibility_device,
		os: input.eligibility_os,
	});
	const body = {
		offer_id: input.offer_id,
		weight: input.weight,
		payout_cents: input.payout_cents,
		active: input.active,
		eligibility,
	};
	return apiClient.post<SmartLinkCandidateRow>(
		`${BASE}/${encodeURIComponent(smartlinkId)}/candidates`,
		body,
	);
}

/** Legacy alias. */
export const attachOffer = attachCandidate;

export async function removeCandidate(
	smartlinkId: string,
	candidateId: string,
): Promise<void> {
	try {
		await apiClient.delete(
			`${BASE}/${encodeURIComponent(smartlinkId)}/candidates/${encodeURIComponent(
				candidateId,
			)}`,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export async function resolveBySlug(
	input: ResolveContextInput,
): Promise<ResolveResponse> {
	const context: ResolveContext = {};
	if (input.geo) context.geo = input.geo;
	if (input.device) context.device = input.device;
	if (input.os) context.os = input.os;
	return apiClient.post<ResolveResponse>(`${BASE}/resolve`, {
		slug: input.slug,
		context,
	});
}

/** Legacy — GET /:id/resolve still works. */
export async function resolveSmartlink(
	smartlinkId: string,
): Promise<ResolveResponse> {
	return apiClient.get<ResolveResponse>(
		`${BASE}/${encodeURIComponent(smartlinkId)}/resolve`,
	);
}

export async function deleteSmartlink(smartlinkId: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(smartlinkId)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

/**
 * Fire `times` resolves in parallel and return the full ordered response
 * array. Errors short-circuit so the caller can surface them in a toast.
 */
export async function resolvePreview(
	smartlinkId: string,
	times: number,
): Promise<ResolveResponse[]> {
	const calls = Array.from({ length: Math.max(0, times) }, () =>
		resolveSmartlink(smartlinkId),
	);
	return Promise.all(calls);
}

export const smartlinksRouterQueryKeys = {
	all: ["smartlinks-router"] as const,
	list: () => ["smartlinks-router", "list"] as const,
	resolve: (id: string) => ["smartlinks-router", "resolve", id] as const,
};
